import { createContext, useContext, useEffect, useState } from "react";

/**
 * Theme provider for the new UI. DARK is the default (team convention:
 * "dark mode first"). The choice persists in localStorage and is applied to
 * <html data-theme="..."> so CSS variables in app.css switch.
 *
 * Also owns the BRAND ACCENT (Settings → Brand accent). The chosen colour is
 * written to the accent CSS variables on <html> so the whole app re-tints, and
 * persists in localStorage so it survives reloads.
 */
const STORAGE_KEY = "theme";
const ACCENT_KEY = "accent";
const DEFAULT_THEME = "dark";
const DEFAULT_ACCENT = "#09A7DF";

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  toggleTheme: () => {},
  setTheme: () => {},
  accent: DEFAULT_ACCENT,
  setAccent: () => {},
});

function getInitialTheme() {
  if (typeof window === "undefined") return DEFAULT_THEME;
  const saved = window.localStorage.getItem(STORAGE_KEY);
  return saved === "light" || saved === "dark" ? saved : DEFAULT_THEME;
}

function getInitialAccent() {
  if (typeof window === "undefined") return DEFAULT_ACCENT;
  const saved = window.localStorage.getItem(ACCENT_KEY);
  return /^#[0-9a-fA-F]{6}$/.test(saved || "") ? saved : DEFAULT_ACCENT;
}

// Write the accent + derived tint variables onto <html>.
export function applyAccent(color) {
  if (!/^#[0-9a-fA-F]{6}$/.test(color || "")) return;
  const root = document.documentElement.style;
  root.setProperty("--brand", color);
  root.setProperty("--brand-600", color);
  root.setProperty("--accent", color);
  root.setProperty("--brand-50", `${color}22`);
  root.setProperty("--accent-subtle", `${color}14`);
  root.setProperty("--accent-tint", `${color}28`);
  root.setProperty("--accent-glow", `${color}66`);
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);
  const [accent, setAccentState] = useState(getInitialAccent);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    applyAccent(accent);
    window.localStorage.setItem(ACCENT_KEY, accent);
  }, [accent]);

  const setTheme = (next) => setThemeState(next === "light" ? "light" : "dark");
  const toggleTheme = () => setThemeState((t) => (t === "dark" ? "light" : "dark"));
  const setAccent = (color) => { if (/^#[0-9a-fA-F]{6}$/.test(color || "")) setAccentState(color); };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme, accent, setAccent }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

export default ThemeContext;
